import { action, computed, makeObservable, observable } from 'mobx';
import type ft from 'firebase';
import { db as firestoreDb, FieldValue } from 'firebase-modules';
import { monotonicFactory, ulid } from 'ulid';
import { matchesAnyNormalizedSearchTerm, splitNormalizedSearchTerms } from 'utils/textSearch';

import type { RootStore } from 'logic';
import {
  BaseSource,
  FirestoreSource,
  FirestoreSourceRow,
  RowData,
  safeBatch,
  SelectOption,
  SourceDisplayMode,
  SourceSearchSetting,
} from '@creative-kit/shared';

import { db } from './data';
import { Row } from './row';
import type { SortColumn } from './ui-source-sort';
import { shouldApplyLibrarySearchToSourceRows } from './source-row-filtering';

export const ADD_NEW_COLUMN_KEY = '__CQ_ADD_FIELD_COLUMN';

export type SourceFieldType =
  | 'text'
  | 'checkbox'
  | 'number'
  | 'url'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'image';

// Preset color palette for select options
export const SELECT_OPTION_COLORS = [
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#6366f1', // indigo
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#737373', // gray
];
export const DEFAULT_NEW_COLUMN_NAME = 'New Column';

export const MODULE_POWER_COLUMN_KEY = '__CQ_MODULE_POWER_COLUMN';

export const DELETE_ROW_COLUMN_KEY = '__CQ_DELETE_ROW_COLUMN';

export const IMAGE_COLUMN_KEY = '__CQ_IMAGE_COLUMN';

export const ATTACHMENT_COLUMN_KEY = '__CQ_ATTACHMENT_COLUMN';
export const DOWNLOADS_COLUMN_KEY = '__CQ_DOWNLOADS_COLUMN';

export interface FieldDescription {
  fieldId: string;
  fieldName: string;
}
export class Source extends BaseSource {
  private stopListeningToRows = () => {};
  private rootStore: RootStore;
  private ref: ft.firestore.DocumentReference<FirestoreSource>;
  rows: Record<string, Row> = {};

  constructor(id: string, sessionId: string, data: FirestoreSource, rootStore: RootStore) {
    super(id, sessionId, data);

    makeObservable(this, {
      data: observable.struct,
      rows: observable,
      updateData: action,
      updateRows: action.bound,
      deleteColumn: action.bound,
      orderedRows: computed,
      filteredRows: computed,
      visibleColumns: computed,
      sourceCount: computed,
      showPowerColumn: computed,
      getAddedColumns: action.bound,
      fields: computed,
      searchResults: computed,
      fieldIdsWithResults: computed,
      searchByCardConnections: computed,
      toggleSearchByCardConnections: action.bound,
      cardMatchedRowIds: computed,
      libraryCardMatchedCount: computed,
      setFieldIds: action.bound,
      hasPrimaryImages: computed,
    });

    this.rootStore = rootStore;
    this.ref = db.sessionSources(this.sessionId).doc(this.id);

    // Migrate: ensure image sources have the Folder field
    if (
      data.type === 'image' &&
      !data.fieldNames?.folderName &&
      !data.fieldIds?.includes('folderName')
    ) {
      this.ref.set(
        {
          fieldNames: { ...data.fieldNames, folderName: 'Folder' },
          fieldIds: [...(data.fieldIds || []), 'folderName'],
        },
        { merge: true },
      );
    }
  }

  getFieldInfo = (fieldId: string): FieldDescription => {
    if (fieldId === MODULE_POWER_COLUMN_KEY) {
      return {
        fieldId: MODULE_POWER_COLUMN_KEY,
        fieldName: 'Action Column',
      };
    }

    return {
      fieldId,
      fieldName: this.data.fieldNames[fieldId],
    };
  };

  get orderedRows() {
    const filteredRows = Object.values(this.rows).filter((r) => !r.isDeleted);

    // Check the sort preference for all source types
    if (this.rootStore.uiSourceSortStore) {
      const isNewestFirst = this.rootStore.uiSourceSortStore.isNewestFirst(this.id);
      return filteredRows.sort((r1, r2) => {
        // ULID comparison: if newest first, reverse the comparison
        return isNewestFirst ? (r1.id > r2.id ? -1 : 1) : r1.id > r2.id ? 1 : -1;
      });
    }

    // Fallback to original order (oldest first) if store isn't available
    return filteredRows.sort((r1, r2) => (r1.id > r2.id ? 1 : -1));
  }

  get searchResults() {
    return this.getSearchResults();
  }

  get filteredRowsWithAllFields() {
    return this.getSearchResults(true).filteredRows;
  }

  get hasFilteredRowsWithAllFields() {
    return this.filteredRowsWithAllFields.length > 0;
  }

  getSearchResults(searchAll?: boolean) {
    const searchString = this.rootStore.uiSourceSearchStore.searchesById[this.id];
    if (!searchString || typeof searchString !== 'string') {
      return {
        filteredRows: this.orderedRows,
        fieldIdsWithResults: [],
        cardMatchedRowIds: new Set<string>(),
      };
    }

    // Match library search term processing: split by comma only, not spaces
    const terms = splitNormalizedSearchTerms(searchString);
    if (!terms.length) {
      return {
        filteredRows: this.orderedRows,
        fieldIdsWithResults: [],
        cardMatchedRowIds: new Set<string>(),
      };
    }

    const powerColumn = this.rootStore.uiModulePowerColumnStore.getSourceModulePowerColumn(this.id);

    const fieldIdsWithResults = new Set<string>();
    const cardMatchedRowIds = new Set<string>();

    const filteredRows = this.orderedRows.filter((row) => {
      let rowMatches = false;
      let cardMatch = false;

      this.getSearchColumns(searchAll).forEach((col) => {
        if (col.fieldId === MODULE_POWER_COLUMN_KEY) {
          const countString = (
            powerColumn?.getCurrentModuleLinkCount(row.id)?.total || 0
          ).toString();
          if (terms.includes(countString)) {
            rowMatches = true;
            fieldIdsWithResults.add(col.fieldId);
          }
        } else if (matchesAnyNormalizedSearchTerm(row.getValue(col.fieldId), terms)) {
          rowMatches = true;
          fieldIdsWithResults.add(col.fieldId);
        }
      });

      // Search by card connections if enabled and no match yet
      if (!rowMatches && this.searchByCardConnections && powerColumn?.selectedModule) {
        const module = powerColumn.selectedModule;
        const { cardsStore, sessionsStore } = this.rootStore;
        const { currentSession } = sessionsStore;

        if (currentSession) {
          // Find all cards connected to this row via the current module
          const connectedCards = Object.values(cardsStore.cards).filter((card) => {
            const connectedRowIds = card.getConnectedRows(module.id);
            return connectedRowIds.includes(row.id);
          });

          // Search through all module connections and field values of connected cards
          for (const card of connectedCards) {
            // Check all modules in the session
            for (const sessionModule of currentSession.modules) {
              const moduleSource = this.rootStore.sourcesStore.sources[sessionModule.sourceId];
              if (!moduleSource) continue;

              // Get connected rows for this module
              const connectedRowIds = card.getConnectedRows(sessionModule.id);

              // Search through field values of all connected rows
              for (const connectedRowId of connectedRowIds) {
                const connectedRow = moduleSource.rows[connectedRowId];
                if (!connectedRow) continue;

                // Search through all field values
                for (const fieldId of moduleSource.data.fieldIds) {
                  const value = connectedRow.getValue(fieldId);
                  if (value && matchesAnyNormalizedSearchTerm(value, terms)) {
                    cardMatch = true;
                    break;
                  }
                }
                if (cardMatch) break;
              }
              if (cardMatch) break;
            }
            if (cardMatch) break;
          }

          if (cardMatch) {
            cardMatchedRowIds.add(row.id);
            rowMatches = true;
          }
        }
      }

      return rowMatches;
    });

    return {
      filteredRows,
      fieldIdsWithResults: Array.from(fieldIdsWithResults),
      cardMatchedRowIds,
    };
  }

  get filteredRows() {
    let rows = this.searchResults.filteredRows;

    // Apply field-value filters (pass fieldIds to skip orphaned filters from deleted columns)
    if (this.rootStore.uiSourceFilterStore.hasFilters(this.id)) {
      const validFieldIds = new Set(this.data.fieldIds ?? []);
      rows = rows.filter((row) =>
        this.rootStore.uiSourceFilterStore.matchesRow(
          this.id,
          (fieldId) => row.getValue(fieldId),
          validFieldIds,
        ),
      );
    }

    // Apply column-based sorting (Airtable-style header click)
    const columnSorts = this.rootStore.uiSourceSortStore.getColumnSorts(this.id);
    if (columnSorts.length > 0) {
      rows = this.applySorts(rows, columnSorts);
    }

    // Library search is for the sources browser/search surface. Once a specific source is open,
    // row visibility should be driven only by source-local filters/search.
    if (
      shouldApplyLibrarySearchToSourceRows({
        hasLibrarySearch: this.rootStore.uiLibrarySearchStore.hasLibrarySearch,
        currentSourceTab: this.rootStore.uiActiveSessionStore.currentSourceTab,
      })
    ) {
      const libraryResults = this.getLibrarySearchResults();
      const matchingRowIds = new Set(libraryResults.matchingRows.map((r) => r.id));
      rows = rows.filter((row) => matchingRowIds.has(row.id));
    }

    // If "show only errors" is enabled, filter to only rows with upload errors
    if (this.rootStore.uiImageSourceUpload?.showOnlyErrorRows[this.id]) {
      const errorRowIds = this.rootStore.uiImageSourceUpload.getRowIdsWithErrors(this.id);
      rows = rows.filter((row) => errorRowIds.has(row.id));
    }

    // If "show only unused rows" is enabled, filter to only rows with no card connections
    if (this.rootStore.uiSourceSearchStore.showOnlyUnusedRowsById[this.id]) {
      rows = rows.filter((row) => row.connectedCards.length === 0);
    }

    return rows;
  }

  private applySorts(rows: Row[], sorts: SortColumn[]): Row[] {
    return [...rows].sort((a, b) => {
      for (const sort of sorts) {
        const aVal = a.getValue(sort.columnKey) ?? '';
        const bVal = b.getValue(sort.columnKey) ?? '';
        const cmp = String(aVal).localeCompare(String(bVal));
        if (cmp !== 0) return sort.direction === 'ASC' ? cmp : -cmp;
      }
      return 0;
    });
  }

  get fieldIdsWithResults() {
    return this.searchResults.fieldIdsWithResults;
  }

  get searchSetting(): SourceSearchSetting {
    const { userSettings } = this.rootStore.userSettingsStore;
    return userSettings.sourceSearch?.[this.id] || { type: 'all' };
  }

  getSearchColumns(searchAll?: boolean) {
    if (searchAll || this.searchSetting.type === 'all') {
      return this.data.fieldIds.concat([MODULE_POWER_COLUMN_KEY]).map(this.getFieldInfo);
    }

    if (this.searchSetting.type === 'visible') {
      return [...this.activeFields, ...(this.showPowerColumn ? [MODULE_POWER_COLUMN_KEY] : [])].map(
        this.getFieldInfo,
      );
    }

    return this.searchSetting.fieldIds.map(this.getFieldInfo);
  }

  get ownFields() {
    return this.data.fieldIds.map(this.getFieldInfo);
  }

  get fields() {
    return [MODULE_POWER_COLUMN_KEY, ...this.data.fieldIds].map(this.getFieldInfo);
  }

  get activeFields() {
    const { currentSession } = this.rootStore.sessionsStore;
    const { fieldsById } = this.rootStore.uiSourceFieldsStore;
    const activeFields =
      (currentSession && fieldsById[currentSession.id]?.[this.id]?.fields) || this.defaultFieldIds;
    return this.data.fieldIds.filter((f) => activeFields.includes(f));
  }

  get visibleFields() {
    const { currentSession } = this.rootStore.sessionsStore;
    const { fieldsById } = this.rootStore.uiSourceFieldsStore;
    const activeFields =
      (currentSession && fieldsById[currentSession.id]?.[this.id]?.fields) || this.defaultFieldIds;
    return this.data.fieldIds.filter(
      (f) =>
        (activeFields.includes(f) || this.fieldIdsWithResults.includes(f)) &&
        f !== this.attachmentFieldId,
    );
  }

  get visibleColumns() {
    return this.visibleFields.map(this.getFieldInfo);
  }

  get showPowerColumn(): boolean {
    const { currentSession } = this.rootStore.sessionsStore;
    const { fieldsById } = this.rootStore.uiSourceFieldsStore;
    const currentShowPowerColumn = currentSession
      ? fieldsById[currentSession.id]?.[this.id]?.showPowerColumn
      : undefined;
    if (currentShowPowerColumn === undefined) {
      return true;
    }

    return currentShowPowerColumn || this.fieldIdsWithResults.includes(MODULE_POWER_COLUMN_KEY);
  }

  get showAttachmentColumn(): boolean {
    const { currentSession } = this.rootStore.sessionsStore;
    const { fieldsById } = this.rootStore.uiSourceFieldsStore;
    const currentShowAttachmentColumn = currentSession
      ? fieldsById[currentSession.id]?.[this.id]?.showAttachmentColumn
      : undefined;
    if (currentShowAttachmentColumn === undefined) {
      return true;
    }

    return currentShowAttachmentColumn;
  }

  get showDownloadsColumn(): boolean {
    const { currentSession } = this.rootStore.sessionsStore;
    const { fieldsById } = this.rootStore.uiSourceFieldsStore;
    const currentShowDownloadsColumn = currentSession
      ? fieldsById[currentSession.id]?.[this.id]?.showDownloadsColumn
      : undefined;
    // Hidden by default
    return currentShowDownloadsColumn ?? false;
  }

  get sourceCount() {
    const {
      uiSourceSearchStore: { getHasSearch },
    } = this.rootStore;

    const allRows = this.orderedRows.length;

    if (!getHasSearch(this.id)) {
      return `${allRows}`;
    }

    const visibleRows = this.filteredRows.length;
    return `${visibleRows}/${allRows}`;
  }

  updateData(newData: FirestoreSource) {
    this.data = newData;
  }

  startListening() {
    this.stopListeningToRows();
    this.stopListeningToRows = db
      .sessionSourceRows(this.sessionId, this.id)
      .onSnapshot(this.updateRows);
  }

  stopListening() {
    this.stopListeningToRows();
  }

  updateRows(snapshot: ft.firestore.QuerySnapshot<FirestoreSourceRow>) {
    snapshot.docChanges().forEach((change) => {
      const changeType = change.type;
      const changeDoc = change.doc;
      if (changeType === 'added' || changeType === 'modified') {
        this.rows[changeDoc.id] = new Row(
          changeDoc.id,
          this.id,
          this.sessionId,
          changeDoc.data(),
          this.rootStore,
        );
      } else {
        delete this.rows[changeDoc.id];
      }
    });
  }

  async addNewRows(
    rows: RowData[],
    { addEmptyRows }: { addEmptyRows: boolean } = { addEmptyRows: false },
  ) {
    const BATCH_LIMIT = 250;
    const batches = [];
    const ulidFactory = monotonicFactory();
    const rowsCollection = db.sessionSourceRows(this.sessionId, this.id);

    const finalRows = rows
      .filter((row) => Object.values(row).some((d) => typeof d === 'string' && d.length > 0))
      .map((row) =>
        Object.entries(row).reduce(
          (finalRow, [key, value]) => ({
            ...finalRow,
            ...(value !== undefined ? { [key]: value } : {}),
          }),
          {} as RowData,
        ),
      );

    while (finalRows.length > 0) {
      const slice = finalRows.splice(0, BATCH_LIMIT);
      const batch = firestoreDb.batch();
      batches.push(batch);
      for (const row of slice) {
        const rowDoc = rowsCollection.doc(ulidFactory());
        const isRowEmpty = Object.values(row).every((value) => value === '');
        if (addEmptyRows || !isRowEmpty) {
          batch.set(rowDoc, { fieldValues: row });
        }
      }
    }
    return Promise.all(batches.map((b) => b.commit()));
  }

  async bulkDeleteRows(rowIds: string[]): Promise<void> {
    for (let i = 0; i < rowIds.length; i += 500) {
      const chunk = rowIds.slice(i, i + 500);
      const batch = firestoreDb.batch();
      for (const rowId of chunk) {
        const rowRef = db.sessionSourceRows(this.sessionId, this.id).doc(rowId);
        batch.update(rowRef, { isDeleted: true });
      }
      await batch.commit();
    }
  }

  async deleteFieldId(deleteFieldId: string) {
    // Remove column value from every source row
    const originalColumnValues: Record<string, string> = {};
    const originalModuleFields: Record<string, string[]> = {};

    const isImageColumn = this.isImageField(deleteFieldId);

    await safeBatch(firestoreDb, Object.values(this.rows), (batch, row) => {
      originalColumnValues[row.id] = row.getFieldValues()[deleteFieldId];

      const rowRef = db.sessionSourceRows(this.sessionId, this.id).doc(row.id);
      const rowUpdate: Record<string, unknown> = {
        fieldValues: {
          ...row.getFieldValues(),
          [deleteFieldId]: FieldValue.delete() as unknown as string,
        },
      };

      // Clean up fieldImages for image columns
      if (isImageColumn) {
        rowUpdate[`fieldImages.${deleteFieldId}`] = FieldValue.delete();
      }

      batch.set(rowRef, rowUpdate, { merge: true });
    });

    const { currentSession } = this.rootStore.sessionsStore;
    const { getModulesForSource, upsertModule } = currentSession!;

    // Go through modules for this source and delete any that reference this column
    getModulesForSource(this.id).forEach((module) => {
      originalModuleFields[module.id] = module.fields;
      const updatedFields = module.fields.filter((id) => id !== deleteFieldId);
      upsertModule({
        ...module,
        moduleId: module.id,
        fields: updatedFields,
      });
    });

    // Delete column from this source
    const originalFieldIds = [...this.data.fieldIds];
    const originalFieldNames = { ...this.data.fieldNames };
    const originalCheckboxFieldIds = [...this.checkboxFieldIds];
    const originalNumberFieldIds = [...this.numberFieldIds];
    const originalUrlFieldIds = [...this.urlFieldIds];
    const originalSelectFieldIds = [...this.selectFieldIds];
    const originalMultiselectFieldIds = [...this.multiselectFieldIds];
    const originalDateFieldIds = [...this.dateFieldIds];
    const originalImageFieldIds = [...this.imageFieldIds];
    const originalSelectFieldOptions = { ...this.data.selectFieldOptions };

    const fieldIds = originalFieldIds.filter((id) => id !== deleteFieldId);
    const fieldNames = { ...this.data.fieldNames };
    delete fieldNames[deleteFieldId];

    // Also clean up field type arrays
    const checkboxFieldIds = originalCheckboxFieldIds.filter((id) => id !== deleteFieldId);
    const numberFieldIds = originalNumberFieldIds.filter((id) => id !== deleteFieldId);
    const urlFieldIds = originalUrlFieldIds.filter((id) => id !== deleteFieldId);
    const selectFieldIds = originalSelectFieldIds.filter((id) => id !== deleteFieldId);
    const multiselectFieldIds = originalMultiselectFieldIds.filter((id) => id !== deleteFieldId);
    const dateFieldIds = originalDateFieldIds.filter((id) => id !== deleteFieldId);
    const imageFieldIds = originalImageFieldIds.filter((id) => id !== deleteFieldId);
    const selectFieldOptions = { ...originalSelectFieldOptions };
    delete selectFieldOptions[deleteFieldId];

    // Check if we're deleting the attachment field
    const isDeletingAttachmentField = this.data.attachmentFieldId === deleteFieldId;
    const originalAttachmentFieldId = this.data.attachmentFieldId;

    const updateData: Record<string, unknown> = {
      fieldIds,
      fieldNames,
      checkboxFieldIds,
      numberFieldIds,
      urlFieldIds,
      selectFieldIds,
      multiselectFieldIds,
      dateFieldIds,
      imageFieldIds,
      selectFieldOptions,
    };

    // Clear attachmentFieldId if we're deleting it
    if (isDeletingAttachmentField) {
      updateData.attachmentFieldId = FieldValue.delete();
    }

    this.ref.set(updateData, { merge: true });

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const undoData: Record<string, unknown> = {
        fieldIds: originalFieldIds,
        fieldNames: originalFieldNames,
        checkboxFieldIds: originalCheckboxFieldIds,
        numberFieldIds: originalNumberFieldIds,
        urlFieldIds: originalUrlFieldIds,
        selectFieldIds: originalSelectFieldIds,
        multiselectFieldIds: originalMultiselectFieldIds,
        dateFieldIds: originalDateFieldIds,
        imageFieldIds: originalImageFieldIds,
        selectFieldOptions: originalSelectFieldOptions,
      };

      // Restore attachmentFieldId if we deleted it
      if (isDeletingAttachmentField && originalAttachmentFieldId) {
        undoData.attachmentFieldId = originalAttachmentFieldId;
      }

      this.ref.set(undoData, { merge: true });

      await safeBatch(firestoreDb, Object.values(this.rows), (batch, row) => {
        const rowRef = db.sessionSourceRows(this.sessionId, this.id).doc(row.id);
        batch.set(
          rowRef,
          {
            fieldValues: {
              ...row.getFieldValues(),
              [deleteFieldId]: originalColumnValues[row.id],
            },
          },
          { merge: true },
        );
      });

      getModulesForSource(this.id).forEach((module) => {
        const originalFields = originalModuleFields[module.id];
        upsertModule({
          ...module,
          moduleId: module.id,
          fields: originalFields,
        });
      });
    }, `Column ${originalFieldNames[deleteFieldId]} has been deleted from ${this.data.name}`);
  }

  addEmptyRow() {
    const rowsCollection = db.sessionSourceRows(this.sessionId, this.id);
    const newRow = rowsCollection.doc(ulid());
    newRow.set({
      fieldValues: {},
    });
    return newRow.id;
  }

  /**
   * Create a row with field values set up front, returning its id. Unlike
   * `addEmptyRow()` + `rows[id].editRowData(...)`, this avoids the race where the
   * local `Row` isn't loaded yet (the row only appears after the Firestore
   * snapshot). Used by the Whole View to author a named group/look in one write.
   */
  addRow(fieldValues: Record<string, string>) {
    const rowsCollection = db.sessionSourceRows(this.sessionId, this.id);
    const newRow = rowsCollection.doc(ulid());
    newRow.set({
      fieldValues,
    });
    return newRow.id;
  }

  addEmptyImageRow() {
    const rowsCollection = db.sessionSourceRows(this.sessionId, this.id);
    const newRow = rowsCollection.doc(ulid());
    // Initialize fieldValues with empty strings for image source fields
    const fieldValues: Record<string, string> = {};
    this.data.fieldIds.forEach((fieldId) => {
      fieldValues[fieldId] = '';
    });
    newRow.set({
      fieldValues,
    });
    return newRow.id;
  }

  /** Legacy Thumbnail column (attachmentFieldId). New sources use addImageColumn + imageFieldIds. */
  addAttachmentField() {
    if (this.hasAttachmentField) {
      return this.data.attachmentFieldId;
    }

    const attachmentFieldId = ulid();
    this.ref.set(
      {
        attachmentFieldId,
        fieldNames: {
          ...this.data.fieldNames,
          [attachmentFieldId]: 'Thumbnail',
        },
      },
      { merge: true },
    );
    return attachmentFieldId;
  }

  get hasAttachmentField() {
    return !!this.data.attachmentFieldId;
  }

  get attachmentFieldId() {
    return this.data.attachmentFieldId;
  }

  // ============================================
  // Checkbox field support
  // ============================================

  get checkboxFieldIds(): string[] {
    return this.data.checkboxFieldIds ?? [];
  }

  isCheckboxField(fieldId: string): boolean {
    return this.checkboxFieldIds.includes(fieldId);
  }

  /**
   * Add a new checkbox column immediately.
   * @param name - The display name for the new checkbox column
   * @returns The new field ID
   */
  async addCheckboxColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        checkboxFieldIds: [...this.checkboxFieldIds, fieldId],
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedCheckboxFieldIds = this.checkboxFieldIds.filter((id) => id !== fieldId);

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          checkboxFieldIds: updatedCheckboxFieldIds,
        },
        { merge: true },
      );
    }, `Checkbox column "${name}" created`);

    return fieldId;
  }

  /**
   * Toggle a checkbox field value for a row.
   * Saves immediately without requiring edit mode.
   * Uses optimistic UI to prevent race conditions on rapid clicks.
   * @param rowId - The row to update
   * @param fieldId - The checkbox field to toggle
   */
  async toggleCheckbox(rowId: string, fieldId: string): Promise<void> {
    const row = this.rows[rowId];
    if (!row) return;

    const currentValue = row.getValue(fieldId);
    const newValue = currentValue === 'true' ? 'false' : 'true';

    // Optimistic update - update local state immediately to prevent race condition
    // on rapid clicks (MobX observable.struct requires new object reference)
    const currentFieldValues = row.getFieldValues();
    const newFieldValues = {
      ...currentFieldValues,
      [fieldId]: newValue,
    };

    // Construct new row data using public getters (data is protected)
    row.updateData({
      fieldValues: newFieldValues,
      image: row.image,
      attachment: row.attachment,
      isDeleted: row.isDeleted,
    });

    // Persist to Firestore (listener will confirm the update)
    const rowRef = db.sessionSourceRows(this.sessionId, this.id).doc(rowId);
    try {
      await rowRef.set(
        {
          fieldValues: newFieldValues,
        },
        { merge: true },
      );
    } catch (error) {
      // Revert on failure using public getters
      row.updateData({
        fieldValues: {
          ...currentFieldValues,
          [fieldId]: currentValue ?? '',
        },
        image: row.image,
        attachment: row.attachment,
        isDeleted: row.isDeleted,
      });
      throw error;
    }
  }

  // Number field type support
  get numberFieldIds(): string[] {
    return this.data.numberFieldIds ?? [];
  }

  isNumberField(fieldId: string): boolean {
    return this.numberFieldIds.includes(fieldId);
  }

  // URL field type support
  get urlFieldIds(): string[] {
    return this.data.urlFieldIds ?? [];
  }

  isUrlField(fieldId: string): boolean {
    return this.urlFieldIds.includes(fieldId);
  }

  // Select field type support
  get selectFieldIds(): string[] {
    return this.data.selectFieldIds ?? [];
  }

  isSelectField(fieldId: string): boolean {
    return this.selectFieldIds.includes(fieldId);
  }

  getSelectOptions(fieldId: string): SelectOption[] {
    return this.data.selectFieldOptions?.[fieldId] ?? [];
  }

  getSelectOptionByValue(fieldId: string, value: string): SelectOption | undefined {
    return this.getSelectOptions(fieldId).find((opt) => opt.label === value);
  }

  /**
   * Add a new select column immediately without entering edit mode.
   * @param name - The display name for the new column
   * @returns The new field ID
   */
  async addSelectColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        selectFieldIds: [...this.selectFieldIds, fieldId],
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: [],
        },
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedSelectFieldIds = this.selectFieldIds.filter((id) => id !== fieldId);
      const updatedSelectOptions = { ...this.data.selectFieldOptions };
      delete updatedSelectOptions[fieldId];

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          selectFieldIds: updatedSelectFieldIds,
          selectFieldOptions: updatedSelectOptions,
        },
        { merge: true },
      );
    }, `Select column "${name}" created`);

    return fieldId;
  }

  /**
   * Add a new option to a select field. Auto-assigns color from palette.
   * Returns existing option if label already exists (case-insensitive).
   * @param fieldId - The select field ID
   * @param label - The display label for the new option
   * @returns The new or existing option
   */
  async addSelectOption(fieldId: string, label: string): Promise<SelectOption> {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      throw new Error('Option label cannot be empty');
    }
    if (trimmedLabel.length > 100) {
      throw new Error('Option label cannot exceed 100 characters');
    }

    const existingOptions = this.getSelectOptions(fieldId);

    // Check for duplicate (case-insensitive)
    const existingOption = existingOptions.find(
      (opt) => opt.label.toLowerCase() === trimmedLabel.toLowerCase(),
    );
    if (existingOption) {
      return existingOption;
    }

    // Auto-assign color from palette (cycles through)
    const colorIndex = existingOptions.length % SELECT_OPTION_COLORS.length;
    const color = SELECT_OPTION_COLORS[colorIndex];

    const newOption: SelectOption = {
      id: ulid(),
      label: trimmedLabel,
      color,
    };

    await this.ref.set(
      {
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: [...existingOptions, newOption],
        },
      },
      { merge: true },
    );

    return newOption;
  }

  /**
   * Update an existing select option (label or color).
   * @param fieldId - The select field ID
   * @param optionId - The option ID to update
   * @param updates - Partial updates to apply
   */
  async updateSelectOption(
    fieldId: string,
    optionId: string,
    updates: Partial<Omit<SelectOption, 'id'>>,
  ): Promise<void> {
    const options = this.getSelectOptions(fieldId);
    const updatedOptions = options.map((opt) =>
      opt.id === optionId ? { ...opt, ...updates } : opt,
    );

    await this.ref.set(
      {
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: updatedOptions,
        },
      },
      { merge: true },
    );
  }

  /**
   * Count how many rows are using a particular select option.
   * @param fieldId - The select field ID
   * @param optionLabel - The option label to check
   * @returns The number of rows using this option
   */
  getOptionUsageCount(fieldId: string, optionLabel: string): number {
    const isMultiselect = this.isMultiselectField(fieldId);
    let count = 0;

    for (const row of Object.values(this.rows)) {
      if (row.isDeleted) continue;
      const value = row.getValue(fieldId);
      if (!value) continue;

      if (isMultiselect) {
        const labels = this.parseMultiselectValue(value);
        if (labels.includes(optionLabel)) {
          count++;
        }
      } else {
        if (value === optionLabel) {
          count++;
        }
      }
    }

    return count;
  }

  /**
   * Remove an option from a select field.
   * Note: This doesn't update existing cell values - they'll show as "unknown" chips.
   * @param fieldId - The select field ID
   * @param optionId - The option ID to remove
   */
  async removeSelectOption(fieldId: string, optionId: string): Promise<void> {
    const options = this.getSelectOptions(fieldId);
    const updatedOptions = options.filter((opt) => opt.id !== optionId);

    await this.ref.set(
      {
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: updatedOptions,
        },
      },
      { merge: true },
    );
  }

  /**
   * Reorder options in a select field.
   * @param fieldId - The select field ID
   * @param optionIds - The new order of option IDs
   */
  async reorderSelectOptions(fieldId: string, optionIds: string[]): Promise<void> {
    const options = this.getSelectOptions(fieldId);
    const optionMap = new Map(options.map((opt) => [opt.id, opt]));
    const reorderedOptions = optionIds
      .map((id) => optionMap.get(id))
      .filter((opt): opt is SelectOption => opt !== undefined);

    await this.ref.set(
      {
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: reorderedOptions,
        },
      },
      { merge: true },
    );
  }

  // Date field type support
  get dateFieldIds(): string[] {
    return this.data.dateFieldIds ?? [];
  }

  isDateField(fieldId: string): boolean {
    return this.dateFieldIds.includes(fieldId);
  }

  // Image field type support
  get imageFieldIds(): string[] {
    return this.data.imageFieldIds ?? [];
  }

  isImageField(fieldId: string): boolean {
    return this.imageFieldIds.includes(fieldId);
  }

  get hasImageColumns(): boolean {
    return this.imageFieldIds.length > 0;
  }

  /** True if any row in this source has a primary image (row.image). */
  get hasPrimaryImages(): boolean {
    return Object.values(this.rows).some((row) => row.image != null);
  }

  async addImageColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        imageFieldIds: [...this.imageFieldIds, fieldId],
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedImageFieldIds = this.imageFieldIds.filter((id) => id !== fieldId);

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          imageFieldIds: updatedImageFieldIds,
        },
        { merge: true },
      );
    }, `Image column "${name}" created`);

    return fieldId;
  }

  // Multi-select field type support
  get multiselectFieldIds(): string[] {
    return this.data.multiselectFieldIds ?? [];
  }

  isMultiselectField(fieldId: string): boolean {
    return this.multiselectFieldIds.includes(fieldId);
  }

  /**
   * Add a new multi-select column immediately without entering edit mode.
   * @param name - The display name for the new column
   * @returns The new field ID
   */
  async addMultiselectColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        multiselectFieldIds: [...this.multiselectFieldIds, fieldId],
        selectFieldOptions: {
          ...this.data.selectFieldOptions,
          [fieldId]: [],
        },
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedMultiselectFieldIds = this.multiselectFieldIds.filter((id) => id !== fieldId);
      const updatedSelectOptions = { ...this.data.selectFieldOptions };
      delete updatedSelectOptions[fieldId];

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          multiselectFieldIds: updatedMultiselectFieldIds,
          selectFieldOptions: updatedSelectOptions,
        },
        { merge: true },
      );
    }, `Multi-select column "${name}" created`);

    return fieldId;
  }

  /**
   * Get the field type for a column.
   */
  getFieldType(fieldId: string): SourceFieldType {
    if (this.isImageField(fieldId)) return 'image';
    if (this.isCheckboxField(fieldId)) return 'checkbox';
    if (this.isNumberField(fieldId)) return 'number';
    if (this.isUrlField(fieldId)) return 'url';
    if (this.isDateField(fieldId)) return 'date';
    if (this.isSelectField(fieldId)) return 'select';
    if (this.isMultiselectField(fieldId)) return 'multiselect';
    return 'text';
  }

  /**
   * Migrate a cell value when changing column types.
   * Returns the converted value appropriate for the new type.
   */
  private migrateFieldValue(
    value: unknown,
    fromType: SourceFieldType,
    toType: SourceFieldType,
  ): unknown {
    // Image data lives in fieldImages, not fieldValues
    if (toType === 'image') return null;
    if (fromType === 'image') return '';

    if (value === undefined || value === null || value === '') return value;

    const strValue = String(value);

    // To checkbox: convert truthy strings
    if (toType === 'checkbox') {
      if (fromType === 'checkbox') return value;
      const lower = strValue.toLowerCase().trim();
      return lower === 'true' || lower === 'yes' || lower === '1' || lower === 'x';
    }

    // To number: parse numeric values
    if (toType === 'number') {
      if (fromType === 'number') return value;
      if (fromType === 'date') {
        // Parse date as epoch timestamp
        const d = new Date(strValue);
        return isNaN(d.getTime()) ? null : String(d.getTime());
      }
      const num = parseFloat(strValue);
      return isNaN(num) ? null : String(num);
    }

    // To date: parse date strings
    if (toType === 'date') {
      if (fromType === 'date') return value;
      const d = new Date(strValue);
      if (isNaN(d.getTime())) return null;
      // Store as ISO date string (YYYY-MM-DD)
      return d.toISOString().split('T')[0];
    }

    // To text/url: stringify
    if (toType === 'text' || toType === 'url') {
      if (fromType === 'checkbox') return value ? 'Yes' : 'No';
      if (fromType === 'select' || fromType === 'multiselect') {
        // Extract labels from JSON array
        try {
          const parsed = JSON.parse(strValue);
          if (Array.isArray(parsed)) return parsed.join(', ');
        } catch {
          // Already plain text
        }
      }
      return strValue;
    }

    // To select: use plain string value
    if (toType === 'select') {
      if (fromType === 'select') return value;
      if (fromType === 'multiselect') {
        // Take first value from multi-select JSON array
        try {
          const parsed = JSON.parse(strValue);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
        } catch {
          // Not JSON, use as-is
        }
        return strValue;
      }
      if (fromType === 'checkbox') {
        return value ? 'Yes' : 'No';
      }
      return strValue || null;
    }

    // To multiselect: wrap in JSON array
    if (toType === 'multiselect') {
      if (fromType === 'multiselect') return value;
      if (fromType === 'select') {
        // Wrap single value in array
        return strValue ? JSON.stringify([strValue]) : null;
      }
      if (fromType === 'checkbox') {
        return JSON.stringify([value ? 'Yes' : 'No']);
      }
      return strValue ? JSON.stringify([strValue]) : null;
    }

    return value;
  }

  /**
   * Change the type of an existing column.
   * Removes the field from its current type array and adds it to the new type array.
   * Migrates existing cell values to the new type format.
   */
  async changeFieldType(fieldId: string, newType: SourceFieldType): Promise<void> {
    const currentType = this.getFieldType(fieldId);
    if (currentType === newType) return;

    const fieldName = this.data.fieldNames?.[fieldId] || 'Column';

    // Build update object - remove from old type array, add to new type array
    const update: Record<string, unknown> = {};

    // Build filtered arrays once (used for both remove and add)
    const filteredCheckbox = this.checkboxFieldIds.filter((id) => id !== fieldId);
    const filteredNumber = this.numberFieldIds.filter((id) => id !== fieldId);
    const filteredUrl = this.urlFieldIds.filter((id) => id !== fieldId);
    const filteredSelect = this.selectFieldIds.filter((id) => id !== fieldId);
    const filteredMultiselect = this.multiselectFieldIds.filter((id) => id !== fieldId);
    const filteredDate = this.dateFieldIds.filter((id) => id !== fieldId);
    const filteredImage = this.imageFieldIds.filter((id) => id !== fieldId);

    // Remove from current type array
    if (currentType === 'checkbox') {
      update.checkboxFieldIds = filteredCheckbox;
    } else if (currentType === 'number') {
      update.numberFieldIds = filteredNumber;
    } else if (currentType === 'url') {
      update.urlFieldIds = filteredUrl;
    } else if (currentType === 'date') {
      update.dateFieldIds = filteredDate;
    } else if (currentType === 'image') {
      update.imageFieldIds = filteredImage;
    } else if (currentType === 'select') {
      update.selectFieldIds = filteredSelect;
      // Clean up options when leaving select type (unless going to multiselect)
      if (newType !== 'multiselect') {
        update[`selectFieldOptions.${fieldId}`] = FieldValue.delete();
      }
    } else if (currentType === 'multiselect') {
      update.multiselectFieldIds = filteredMultiselect;
      // Clean up options when leaving multiselect type (unless going to select)
      if (newType !== 'select') {
        update[`selectFieldOptions.${fieldId}`] = FieldValue.delete();
      }
    }

    // Add to new type array (using pre-filtered arrays, no redundant filter needed)
    if (newType === 'checkbox') {
      update.checkboxFieldIds = [...filteredCheckbox, fieldId];
    } else if (newType === 'number') {
      update.numberFieldIds = [...filteredNumber, fieldId];
    } else if (newType === 'url') {
      update.urlFieldIds = [...filteredUrl, fieldId];
    } else if (newType === 'date') {
      update.dateFieldIds = [...filteredDate, fieldId];
    } else if (newType === 'image') {
      update.imageFieldIds = [...filteredImage, fieldId];
    } else if (newType === 'select' || newType === 'multiselect') {
      // Add to appropriate field ID array
      if (newType === 'select') {
        update.selectFieldIds = [...filteredSelect, fieldId];
      } else {
        update.multiselectFieldIds = [...filteredMultiselect, fieldId];
      }

      // Extract unique values from MIGRATED data to create options
      // We need to calculate what the migrated values will be, then extract labels
      const existingOptions = this.data.selectFieldOptions?.[fieldId] ?? [];
      const existingLabels = new Set(existingOptions.map((opt) => opt.label.toLowerCase()));

      // Collect unique non-empty labels from all rows (using migrated values)
      const uniqueLabels = new Set<string>();
      for (const row of Object.values(this.rows)) {
        if (row.isDeleted) continue;
        const rawValue = row.getValue(fieldId);
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;

        // Calculate what the migrated value will be
        const migratedValue = this.migrateFieldValue(rawValue, currentType, newType);
        if (!migratedValue) continue;

        // Extract labels from the migrated JSON array format
        const migratedStr = String(migratedValue);
        if (migratedStr.startsWith('[')) {
          try {
            const parsed = JSON.parse(migratedStr);
            if (Array.isArray(parsed)) {
              parsed.forEach((v) => {
                if (typeof v === 'string' && v.trim()) uniqueLabels.add(v.trim());
              });
            }
          } catch {
            // Not JSON, treat as plain text
            if (migratedStr.trim()) uniqueLabels.add(migratedStr.trim());
          }
        } else if (migratedStr.trim()) {
          uniqueLabels.add(migratedStr.trim());
        }
      }

      // Create options for values that don't already exist
      const newOptions: SelectOption[] = [...existingOptions];
      for (const label of uniqueLabels) {
        if (!existingLabels.has(label.toLowerCase())) {
          const colorIndex = newOptions.length % SELECT_OPTION_COLORS.length;
          newOptions.push({
            id: ulid(),
            label,
            color: SELECT_OPTION_COLORS[colorIndex],
          });
          existingLabels.add(label.toLowerCase());
        }
      }

      update.selectFieldOptions = {
        ...this.data.selectFieldOptions,
        [fieldId]: newOptions,
      };
    }
    // 'text' type doesn't need to be added to any array

    await this.ref.set(update, { merge: true });

    // Migrate existing cell values in rows
    const rowsToMigrate = Object.values(this.rows).filter((row) => {
      const cellValue = row.getValue(fieldId);
      return cellValue !== undefined && cellValue !== null && cellValue !== '';
    });

    if (rowsToMigrate.length > 0) {
      const batch = firestoreDb.batch();
      const rowsCollection = firestoreDb.collection(
        `sessions/${this.sessionId}/sources/${this.id}/rows`,
      );
      for (const row of rowsToMigrate) {
        const currentValue = row.getValue(fieldId);
        const migratedValue = this.migrateFieldValue(currentValue, currentType, newType);
        if (migratedValue !== currentValue) {
          batch.update(rowsCollection.doc(row.id), { [`fieldValues.${fieldId}`]: migratedValue });
        }
      }
      await batch.commit();
    }

    // Show undo toast
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      await this.changeFieldType(fieldId, currentType);
    }, `Changed "${fieldName}" to ${newType}`);
  }

  /**
   * Parse multi-select cell value into array of labels.
   * Values are stored as JSON arrays to handle labels containing commas.
   * Falls back to comma-separated parsing for backwards compatibility.
   */
  parseMultiselectValue(value: string | undefined): string[] {
    if (!value) return [];

    // Try JSON array first (new format)
    if (value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
        }
      } catch {
        // Fall through to legacy parsing
      }
    }

    // Legacy format: comma-separated (for backwards compatibility)
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  /**
   * Format array of labels into multi-select cell value.
   * Uses JSON array format to safely handle labels containing commas.
   */
  formatMultiselectValue(labels: string[]): string {
    if (labels.length === 0) return '';
    return JSON.stringify(labels);
  }

  renameSource(newName: string) {
    return this.ref.set(
      {
        name: newName,
      },
      { merge: true },
    );
  }

  async setDisplayMode(mode: SourceDisplayMode) {
    await this.ref.set({ displayMode: mode }, { merge: true });
  }

  // ============================================
  // Immediate column operations (no edit mode required)
  // ============================================

  /**
   * Add a new text column immediately without entering edit mode.
   * For other column types, use addCheckboxColumn, addNumberColumn, addUrlColumn,
   * addSelectColumn, or addMultiselectColumn.
   * @param name - The display name for the new column
   * @param _type - Reserved for future use
   * @returns The new field ID
   */
  async addColumnImmediate(name: string, _type: string = 'text'): Promise<string> {
    const fieldId = ulid();

    // Update the source with the new field
    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      // Undo: remove the column
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
        },
        { merge: true },
      );
    }, `Column "${name}" created`);

    return fieldId;
  }

  /**
   * Add a new number column immediately without entering edit mode.
   * @param name - The display name for the new column
   * @returns The new field ID
   */
  async addNumberColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        numberFieldIds: [...this.numberFieldIds, fieldId],
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedNumberFieldIds = this.numberFieldIds.filter((id) => id !== fieldId);

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          numberFieldIds: updatedNumberFieldIds,
        },
        { merge: true },
      );
    }, `Number column "${name}" created`);

    return fieldId;
  }

  /**
   * Add a new URL column immediately without entering edit mode.
   * @param name - The display name for the new column
   * @returns The new field ID
   */
  async addUrlColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        urlFieldIds: [...this.urlFieldIds, fieldId],
      },
      { merge: true },
    );

    // Ensure new column is visible if a custom fields list exists
    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedUrlFieldIds = this.urlFieldIds.filter((id) => id !== fieldId);

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          urlFieldIds: updatedUrlFieldIds,
        },
        { merge: true },
      );
    }, `URL column "${name}" created`);

    return fieldId;
  }

  /**
   * Add a new date column immediately without entering edit mode.
   * @param name - The display name for the new column
   * @returns The new field ID
   */
  async addDateColumn(name: string): Promise<string> {
    const fieldId = ulid();

    await this.ref.set(
      {
        fieldIds: [...this.data.fieldIds, fieldId],
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: name,
        },
        dateFieldIds: [...this.dateFieldIds, fieldId],
      },
      { merge: true },
    );

    this.rootStore.uiSourceFieldsStore.ensureFieldVisible(this.id, fieldId);

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      const updatedFieldIds = this.data.fieldIds.filter((id) => id !== fieldId);
      const updatedFieldNames = { ...this.data.fieldNames };
      delete updatedFieldNames[fieldId];
      const updatedDateFieldIds = this.dateFieldIds.filter((id) => id !== fieldId);

      await this.ref.set(
        {
          fieldIds: updatedFieldIds,
          fieldNames: updatedFieldNames,
          dateFieldIds: updatedDateFieldIds,
        },
        { merge: true },
      );
    }, `Date column "${name}" created`);

    return fieldId;
  }

  /**
   * Rename a column immediately without entering edit mode.
   * @param fieldId - The field ID to rename
   * @param newName - The new display name
   */
  async renameColumnImmediate(fieldId: string, newName: string): Promise<void> {
    const originalName = this.data.fieldNames[fieldId];
    if (originalName === newName) return;

    await this.ref.set(
      {
        fieldNames: {
          ...this.data.fieldNames,
          [fieldId]: newName,
        },
      },
      { merge: true },
    );

    // Show undo option
    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      await this.ref.set(
        {
          fieldNames: {
            ...this.data.fieldNames,
            [fieldId]: originalName,
          },
        },
        { merge: true },
      );
    }, `Column renamed to "${newName}"`);
  }

  /**
   * Delete a column immediately without entering edit mode.
   * This is an alias for deleteFieldId for API consistency.
   * @param fieldId - The field ID to delete
   */
  async deleteColumnImmediate(fieldId: string): Promise<void> {
    return this.deleteFieldId(fieldId);
  }

  get isEditModeActive() {
    return !!this.data.editMode;
  }

  get hasEdits() {
    const { editMode } = this.data;
    if (!editMode) {
      return false;
    }

    return (
      this.numFieldsAdded > 0 ||
      this.numRowsDeleted > 0 ||
      this.numCellsModified > 0 ||
      this.numFieldsRenamed > 0 ||
      this.numColumnsDeleted > 0
    );
  }

  get columnsDeleted() {
    return this.data.editMode?.deletedFieldIds ?? [];
  }

  get numColumnsDeleted() {
    return this.columnsDeleted.length;
  }

  get rowsDeleted() {
    return this.data.editMode?.deletedRowIds || [];
  }

  get numRowsDeleted() {
    return this.rowsDeleted.length;
  }

  get rowsModified() {
    return Object.entries(this.data.editMode?.editedRows || {})
      .filter(([rowId, row]) => !this.rowsDeleted.includes(rowId) && Object.keys(row).length > 0)
      .reduce(
        (rows, [rowId, row]) => ({
          ...rows,
          [rowId]: row,
        }),
        {} as Record<string, RowData>,
      );
  }

  get numRowsModified() {
    return Object.values(this.rowsModified).length;
  }

  get numCellsModified() {
    return Object.values(this.rowsModified).flatMap((o) => Object.keys(o)).length;
  }

  get fieldsRenamed() {
    return this.data.editMode?.renamedFields || {};
  }

  get numFieldsRenamed() {
    return Object.keys(this.fieldsRenamed).length;
  }

  get fieldsAdded() {
    return this.data.editMode?.addedFieldIds || [];
  }

  get numFieldsAdded() {
    return this.fieldsAdded.length;
  }

  async setFieldIds(newFieldIds: string[]) {
    const originalFieldIds = [...this.data.fieldIds];
    await this.ref.set({ fieldIds: newFieldIds }, { merge: true });
    this.rootStore.uiSnackbarStore.setUndoDetails(
      async () => this.ref.set({ fieldIds: originalFieldIds }, { merge: true }),
      'Column order updated',
    );
  }

  async setDefaultFields(defaultFields: string[]) {
    await this.ref.set({ defaultFields }, { merge: true });
  }

  updateSource({ fieldIds, fieldNames, name }: FirestoreSource) {
    this.ref.set(
      {
        fieldIds,
        fieldNames,
        name,
      },
      { merge: true },
    );
  }

  // Edit mode actions
  activateEditMode() {
    if (!this.isEditModeActive) {
      this.ref.set(
        {
          editMode: {
            addedFieldIds: [],
            addedFieldNames: {},
            deletedRowIds: [],
            editedRows: {},
            deletedFieldIds: [],
            renamedFields: {},
          },
        },
        { merge: true },
      );
    }
  }

  exitEditMode() {
    // Run in transaction to prevent one user blasting away edit mode before another person is done
    firestoreDb.runTransaction(async (transaction) => {
      transaction.set(
        this.ref,
        {
          editMode: FieldValue.delete() as unknown as undefined,
        },
        { merge: true },
      );
    });
  }

  saveEditMode() {
    firestoreDb.runTransaction(async (transaction) => {
      // TODO: do we need to update delete and edit to run in transaction?
      if (this.data.editMode) {
        // Delete rows
        for (const deletedRowId of this.data.editMode.deletedRowIds ?? []) {
          this.rows[deletedRowId]?.delete();
        }

        // Edit rows
        for (const [rowId, newRowData] of Object.entries(this.rowsModified)) {
          this.rows[rowId].editRowData(newRowData);
        }

        for (const deleteFieldId of this.columnsDeleted) {
          this.deleteFieldId(deleteFieldId);
        }

        // Rename fields, add new fields, and exit edit mode
        transaction.set(
          this.ref,
          {
            editMode: FieldValue.delete() as unknown as undefined,
            fieldIds: FieldValue.arrayUnion(
              ...(this.data.editMode.addedFieldIds ?? []),
            ) as unknown as string[],
            fieldNames: {
              ...this.data.fieldNames,
              ...this.data.editMode.renamedFields,
              ...this.data.editMode.addedFieldNames,
            },
          },
          { merge: true },
        );
      }
    });
  }

  async saveModuleConnectionEdits({
    successMessage,
    moduleId,
    rowId,
  }: {
    successMessage: string;
    moduleId: string;
    rowId: string;
  }) {
    const { editedFieldValues, editedFieldNames, addedFieldIds } =
      this.rootStore.uiModuleSourceEditor.getRowEditor(moduleId, rowId);
    const { currentSession } = this.rootStore.sessionsStore;
    const originalData = {
      fieldNames: { ...this.data.fieldNames },
      rowData: this.rows[rowId].getFieldValues(),
    };

    await this.ref.set(
      {
        fieldIds: FieldValue.arrayUnion(...addedFieldIds) as unknown as string[],
        fieldNames: {
          ...this.data.fieldNames,
          ...editedFieldNames,
        },
      },
      { merge: true },
    );

    addedFieldIds.forEach((field) => {
      currentSession?.switchModuleField({ moduleId, field });
    });

    this.rows[rowId].editRowData(editedFieldValues);

    this.rootStore.uiSnackbarStore.setUndoDetails(() => {
      addedFieldIds.forEach((field) => {
        currentSession?.switchModuleField({ moduleId, field });
      });
      this.ref.set(
        {
          fieldIds: FieldValue.arrayRemove(...addedFieldIds) as unknown as string[],
          fieldNames: originalData.fieldNames,
        },
        { merge: true },
      );
      this.rows[rowId].editRowData(originalData.rowData);
    }, successMessage);
  }

  isColumnMarkedForDeletion = (fieldId: string) =>
    !!this.data.editMode?.deletedFieldIds?.includes(fieldId);

  isRowMarkedForDeletion(rowId: string) {
    return !!this.data.editMode?.deletedRowIds?.includes(rowId);
  }

  isRowEdited(rowId: string) {
    return !!Object.keys(this.data.editMode?.editedRows || {}).includes(rowId);
  }

  isRowModified(rowId: string) {
    return this.isRowMarkedForDeletion(rowId) || this.isRowEdited(rowId);
  }

  deleteRow(rowId: string) {
    this.ref.update({
      'editMode.deletedRowIds': FieldValue.arrayUnion(rowId),
    });
  }

  deleteColumn(fieldId: string) {
    this.ref.update({
      'editMode.deletedFieldIds': FieldValue.arrayUnion(fieldId),
    });
  }

  revertRowModifications(rowId: string) {
    this.ref.update({
      'editMode.deletedRowIds': FieldValue.arrayRemove(rowId),
      [`editMode.editedRows.${rowId}`]: FieldValue.delete(),
    });
  }

  getCellInitialValueForEditing(rowId: string, fieldId: string) {
    return this.getCellEditedValue(rowId, fieldId) ?? this.getCellCurrentValue(rowId, fieldId);
  }

  getCellEditedValue(rowId: string, fieldId: string) {
    return this.data.editMode?.editedRows?.[rowId]?.[fieldId] ?? null;
  }

  getCellCurrentValue(rowId: string, fieldId: string) {
    return this.rows[rowId].getValue(fieldId);
  }

  isCellEdited(rowId: string, fieldId: string) {
    return this.getCellEditedValue(rowId, fieldId) !== null;
  }

  commitCell(rowId: string, fieldId: string, newValue: string) {
    // Check if the commit is on a cell of the "New Column"
    let actualFieldId = fieldId;
    if (fieldId === ADD_NEW_COLUMN_KEY) {
      actualFieldId = this.queueNewColumnForAddition();
    }

    const currentValue = this.getCellCurrentValue(rowId, actualFieldId);
    const hasExistingEdit = this.isCellEdited(rowId, actualFieldId);

    // Skip update if value unchanged and no existing edit to clear
    // This prevents creating editMode object when there's no actual change
    if (currentValue === newValue && !hasExistingEdit) {
      return;
    }

    this.ref.update({
      [`editMode.editedRows.${rowId}.${actualFieldId}`]:
        currentValue === newValue ? FieldValue.delete() : newValue,
    });
  }

  pasteCells = (data: string[][], startPosition: { rowIdx: number; colIdx: number }) => {
    data.forEach((pastedRow, pastedRowIdx) => {
      const targetRow = this.filteredRows[startPosition.rowIdx + pastedRowIdx];
      if (targetRow) {
        pastedRow.forEach((pastedCellValue, pastedCellColIdx) => {
          const targetColumn = this.visibleColumns[startPosition.colIdx + pastedCellColIdx];
          if (targetColumn) {
            this.commitCell(targetRow.id, targetColumn.fieldId, pastedCellValue);
          }
        });
      }
    });
  };

  private onlyQueueNewColumnForAddition() {
    const fieldId = ulid();
    this.ref.update({
      'editMode.addedFieldIds': FieldValue.arrayUnion(fieldId),
      [`editMode.addedFieldNames.${fieldId}`]: DEFAULT_NEW_COLUMN_NAME,
    });

    return fieldId;
  }

  queueNewColumnForAddition() {
    const fieldId = this.onlyQueueNewColumnForAddition();

    this.rootStore.uiSnackbarStore.setUndoDetails(async () => {
      await this.ref.update({
        'editMode.addedFieldIds': FieldValue.arrayRemove(fieldId),
        [`editMode.addedFieldNames.${fieldId}`]: FieldValue.delete(),
      });
    }, 'New column created');

    return fieldId;
  }

  getAddedColumns() {
    return (
      this.data.editMode?.addedFieldIds?.map((fieldId) => ({
        fieldId,
        fieldName: this.data.editMode!.addedFieldNames[fieldId],
      })) || []
    );
  }

  editColumnName(fieldId: string, newName: string) {
    if (this.data.fieldIds.includes(fieldId)) {
      // Existing column being edited
      const currentFieldName = this.data.fieldNames[fieldId];
      if (newName !== currentFieldName) {
        this.ref.update({
          [`editMode.renamedFields.${fieldId}`]: newName,
        });
      } else {
        this.ref.update({
          [`editMode.renamedFields.${fieldId}`]: FieldValue.delete(),
        });
      }
    } else if (this.data.editMode?.addedFieldIds.includes(fieldId)) {
      // Added column being edited
      this.ref.update({
        [`editMode.addedFieldNames.${fieldId}`]: newName,
      });
    }
  }

  getRenamedColumnName(fieldId: string) {
    return this.data.editMode?.renamedFields?.[fieldId] || null;
  }

  toggleSearchSettingCustomField(fieldId: string) {
    if (this.searchSetting.type === 'custom') {
      if (this.searchSetting.fieldIds.includes(fieldId)) {
        this.updateSearchSetting({
          type: 'custom',
          fieldIds: this.searchSetting.fieldIds.filter((f) => f !== fieldId),
        });
      } else {
        this.updateSearchSetting({
          type: 'custom',
          fieldIds: this.searchSetting.fieldIds.concat(fieldId),
        });
      }
    }
  }

  changeSearchSettingType(type: string) {
    if (type === 'all' || type === 'visible') {
      this.updateSearchSetting({
        type,
      });
    } else if (type === 'custom') {
      this.updateSearchSetting({
        type,
        fieldIds: [],
      });
    }
  }

  private updateSearchSetting(newSetting: SourceSearchSetting) {
    this.rootStore.userSettingsStore.modifyUserSettings({
      [`sourceSearch.${this.id}`]: newSetting,
    });
  }

  get searchByCardConnections(): boolean {
    // If explicitly set, use that value
    if (this.data.searchByCardConnections !== undefined) {
      return this.data.searchByCardConnections;
    }

    // Default based on display mode:
    // - Gallery sources: ON (search by card text makes sense for images)
    // - Spreadsheet sources: OFF (text is already in the source itself)
    return this.isGalleryView;
  }

  toggleSearchByCardConnections() {
    this.ref.update({
      searchByCardConnections: !this.searchByCardConnections,
    });
  }

  get filteredCount(): number | undefined {
    const {
      uiSourceSearchStore: { getHasSearch },
    } = this.rootStore;

    if (!getHasSearch(this.id)) {
      return undefined;
    }

    return this.filteredRows.length;
  }

  getLibrarySearchResults() {
    const {
      uiLibrarySearchStore: { librarySearchTerms },
    } = this.rootStore;

    if (!librarySearchTerms.length) {
      return {
        matchingRows: this.orderedRows,
        matchCount: this.orderedRows.length,
        cardMatchedRowIds: new Set<string>(),
      };
    }

    const powerColumn = this.rootStore.uiModulePowerColumnStore.getSourceModulePowerColumn(this.id);
    const cardMatchedRowIds = new Set<string>();

    const matchingRows = this.orderedRows.filter((row) => {
      let rowMatches = false;
      let cardMatch = false;

      // Search through all fields
      this.data.fieldIds.forEach((fieldId) => {
        const value = row.getValue(fieldId);
        if (value && matchesAnyNormalizedSearchTerm(value, librarySearchTerms)) {
          rowMatches = true;
        }
      });

      // Also check power column if it exists
      if (powerColumn) {
        const countString = (powerColumn.getCurrentModuleLinkCount(row.id)?.total || 0).toString();
        if (librarySearchTerms.includes(countString)) {
          rowMatches = true;
        }
      }

      // Search by card connections if enabled
      if (this.searchByCardConnections && powerColumn?.selectedModule && !rowMatches) {
        const module = powerColumn.selectedModule;
        const { cardsStore, sessionsStore } = this.rootStore;
        const { currentSession } = sessionsStore;

        if (currentSession) {
          // Find all cards connected to this row via the current module
          const connectedCards = Object.values(cardsStore.cards).filter((card) => {
            const connectedRowIds = card.getConnectedRows(module.id);
            return connectedRowIds.includes(row.id);
          });

          // Search through all module connections and field values of connected cards
          for (const card of connectedCards) {
            // Check all modules in the session
            for (const sessionModule of currentSession.modules) {
              const moduleSource = this.rootStore.sourcesStore.sources[sessionModule.sourceId];
              if (!moduleSource) continue;

              // Get connected rows for this module
              const connectedRowIds = card.getConnectedRows(sessionModule.id);

              // Search through field values of all connected rows
              for (const connectedRowId of connectedRowIds) {
                const connectedRow = moduleSource.rows[connectedRowId];
                if (!connectedRow) continue;

                // Search through all field values
                for (const fieldId of moduleSource.data.fieldIds) {
                  const value = connectedRow.getValue(fieldId);
                  if (value && matchesAnyNormalizedSearchTerm(value, librarySearchTerms)) {
                    cardMatch = true;
                    break;
                  }
                }
                if (cardMatch) break;
              }
              if (cardMatch) break;
            }
            if (cardMatch) break;
          }

          if (cardMatch) {
            cardMatchedRowIds.add(row.id);
            rowMatches = true;
          }
        }
      }

      return rowMatches;
    });

    return {
      matchingRows,
      matchCount: matchingRows.length,
      cardMatchedRowIds,
    };
  }

  get librarySearchMatchCount() {
    return this.getLibrarySearchResults().matchCount;
  }

  get hasLibrarySearchMatches() {
    return this.librarySearchMatchCount > 0;
  }

  get libraryCardMatchedCount() {
    return this.getLibrarySearchResults().cardMatchedRowIds.size;
  }

  get cardMatchedRowIds(): Set<string> {
    // Combine card-matched row IDs from both library search and source-specific search
    const libraryMatches = this.getLibrarySearchResults().cardMatchedRowIds;
    const sourceMatches = this.searchResults.cardMatchedRowIds;

    // Merge both sets
    return new Set([...libraryMatches, ...sourceMatches]);
  }
}
